import React, { useCallback, useEffect, useState } from "react";
import { useFirebase } from "../../Firebase/FirebaseContext";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  Card,
  CardContent,
  Grid,
  makeStyles,
  Typography,
  Box,
  CircularProgress,
  Fade,
} from "@material-ui/core";
import { MeetingRoom, Info, Dashboard } from "@material-ui/icons";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    animation: "$fadeIn 0.6s ease-out",
  },
  header: {
    marginBottom: theme.spacing(4),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  headerIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    animation: "$float 3s ease-in-out infinite",
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "1.1rem",
  },
  gridContainer: {
    marginTop: theme.spacing(3),
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    borderRadius: 20,
    overflow: "hidden",
    position: "relative",
    background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
    border: `2px solid ${theme.palette.divider}`,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
      transform: "scaleX(0)",
      transformOrigin: "left",
      transition: "transform 0.4s ease",
    },
    "&:hover": {
      transform: "translateY(-12px) scale(1.02)",
      boxShadow: `0 20px 40px -10px ${theme.palette.primary.main}33`,
      borderColor: theme.palette.primary.main,
      "&::before": {
        transform: "scaleX(1)",
      },
      "& $cardIcon": {
        transform: "scale(1.15) rotate(8deg)",
        color: theme.palette.primary.main,
      },
      "& $roomBadge": {
        backgroundColor: theme.palette.primary.main,
        color: "#FFFFFF",
        transform: "scale(1.05)",
      },
    },
  },
  cardContent: {
    padding: theme.spacing(4),
    textAlign: "center",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  cardIcon: {
    fontSize: 56,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))",
  },
  roomName: {
    fontWeight: 700,
    fontSize: "1.4rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    letterSpacing: "0.5px",
  },
  roomBadge: {
    display: "inline-block",
    padding: theme.spacing(0.75, 2.5),
    borderRadius: 24,
    backgroundColor: theme.palette.primary.main + "15",
    color: theme.palette.primary.main,
    fontSize: "0.875rem",
    fontWeight: 600,
    transition: "all 0.3s ease",
    border: `1.5px solid ${theme.palette.primary.main}33`,
  },
  loadingState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    gap: theme.spacing(3),
  },
  loadingIcon: {
    color: theme.palette.primary.main,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(10),
    animation: "$fadeIn 0.8s ease-out",
  },
  emptyIcon: {
    fontSize: 100,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(3),
    opacity: 0.3,
  },
  skeletonCard: {
    height: 200,
    borderRadius: 20,
  },
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
    },
    "100%": {
      opacity: 1,
    },
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px)",
    },
    "50%": {
      transform: "translateY(-10px)",
    },
  },
}));
export default function Home() {
  const { firebase } = useFirebase();
  const history = useHistory();
  const classes = useStyles();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roomsRetryAttempt, setRoomsRetryAttempt] = useState(0);
  const [labRetryAttempt, setLabRetryAttempt] = useState(0); // Compteur de retry pour le lab

  const fetchRooms = useCallback(async () => {
    const currentLab = localStorage.getItem("currentLab");

    if (!currentLab) {
      console.warn("currentLab non défini dans le localStorage.");
      if (labRetryAttempt < 3) {
        setTimeout(() => {
          setLabRetryAttempt((prev) => prev + 1);
          fetchRooms();
        }, 500);
      } else {
        console.error("currentLab non défini après plusieurs tentatives.");
      }
      return;
    }

    try {
      const q = query(
        collection(firebase.db, "rooms"),
        where("laboratoryId", "==", currentLab)
      );
      const querySnapshot = await getDocs(q);
      const arr = [];

      querySnapshot.forEach((doc) => {
        arr.push({ room: doc.data(), id: doc.id });
      });

      arr.sort((a, b) => a.room.name.localeCompare(b.room.name));
      setRooms(arr);
      setLoading(false);
    } catch (err) {
      console.error("Erreur lors du fetch des rooms :", err);
      setLoading(false);
    }
  }, [firebase, labRetryAttempt]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    if (rooms.length === 0 && roomsRetryAttempt < 1 && labRetryAttempt === 0) {
      const timeout = setTimeout(() => {
        setRoomsRetryAttempt((prev) => prev + 1);
        fetchRooms();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [rooms, roomsRetryAttempt, labRetryAttempt, fetchRooms]);

  return (
    <div className={classes.container}>
      <Box className={classes.header}>
        <Dashboard className={classes.headerIcon} />
        <Box>
          <Typography variant="h3" className={classes.title}>
            Accueil
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            Sélectionnez une salle pour afficher l'écran d'attente
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box className={classes.loadingState}>
          <CircularProgress
            size={60}
            className={classes.loadingIcon}
            thickness={4}
          />
          <Typography
            variant="h5"
            style={{ fontWeight: 600, color: "#6B7280" }}
          >
            Chargement des salles...
          </Typography>
        </Box>
      ) : rooms.length === 0 ? (
        <Box className={classes.emptyState}>
          <Info className={classes.emptyIcon} />
          <Typography
            variant="h4"
            gutterBottom
            style={{ fontWeight: 700, color: "#374151" }}
          >
            Aucune salle disponible
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ fontSize: "1.1rem" }}
          >
            Veuillez créer des salles pour commencer
          </Typography>
        </Box>
      ) : (
        <Fade in timeout={600}>
          <Grid container spacing={3} className={classes.gridContainer}>
            {rooms.map((data, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`uniqueKey-${i}`}>
                <Card
                  className={classes.card}
                  onClick={() => {
                    history.push("/waitingScreen", { data: data });
                  }}
                  elevation={0}
                >
                  <CardContent className={classes.cardContent}>
                    <MeetingRoom className={classes.cardIcon} />
                    <Typography className={classes.roomName}>
                      {data.room.name}
                    </Typography>
                    <span className={classes.roomBadge}>Salle active</span>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Fade>
      )}
    </div>
  );
}
